import { useEffect, useState } from "react";
import { Bot, Inbox, Lightbulb, Loader2, RefreshCw, Send, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { streamWrite } from "@/components/AiWriter";
import { UserAvatar } from "@/components/UserBits";
import { timeAgo } from "@/lib/timeAgo";

const Card = ({ icon: Icon, title, children, testId, action }) => (
  <section className="rounded-3xl bg-white border border-pink-100 p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-emerald-600" /><h3 className="font-bold text-lg flex-1">{title}</h3>{action}</div>
    {children}
  </section>
);

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/ai/moderation-stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const box = (l, v, id) => <div className="rounded-2xl bg-emerald-50 p-3 text-center"><p className="font-display text-2xl font-black text-emerald-700" data-testid={`ai-stat-${id}`}>{v}</p><p className="text-[11px] text-slate-500">{l}</p></div>;
  return (
    <Card icon={ShieldCheck} title="Automatische Prüfung" testId="ai-stats">
      <p className="text-xs text-slate-500">ChatGPT prüft jeden neuen Beitrag, Kommentar, Team-Chat und Profiltext sowie neue Konten. Auffälliges landet unter „Meldungen“ – gelöscht wird nichts automatisch.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{box("geprüft", s.checked, "checked")}{box("auffällig", s.flagged, "flagged")}{box("offene Meldungen", s.open, "open")}{box("verdächtige Konten", s.suspicious, "suspicious")}</div>
    </Card>
  );
}

function MoodReport() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (refresh = false) => {
    setBusy(true);
    try { setR((await api.get(`/admin/ai/mood-report${refresh ? "?refresh=true" : ""}`)).data); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card icon={Bot} title="Wochen-Stimmungsbericht" testId="ai-mood" action={r && <button onClick={() => load(true)} disabled={busy} data-testid="ai-mood-refresh" title="Neu erstellen" className="p-2 rounded-full hover:bg-emerald-50 text-emerald-600"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></button>}>
      {r ? <><p className="text-xs text-slate-400">Basierend auf {r.posts} Beiträgen und {r.comments} Kommentaren der letzten 7 Tage</p><p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-mood-text">{r.text}</p></>
        : <button onClick={() => load()} disabled={busy} data-testid="ai-mood-btn" className="h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />} Bericht anzeigen</button>}
    </Card>
  );
}

function Ideas() {
  const [kind, setKind] = useState("ideas");
  const [topic, setTopic] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setOut("");
    try { await streamWrite({ kind, idea: topic.trim() || "freie Wahl" }, (t) => setOut(t.replace(/\*\*/g, ""))); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card icon={Lightbulb} title="Ideen-Werkstatt" testId="ai-ideas">
      <div className="flex gap-2">
        {[["ideas", "Beitrags-Ideen"], ["poll", "Umfrage"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} data-testid={`ai-ideas-kind-${k}`} className={`h-9 px-4 rounded-full text-sm font-bold ${kind === k ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>{l}</button>)}
      </div>
      <div className="flex gap-2">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={300} placeholder="Thema (optional), z. B. Herbst, Urlaub, Essen" data-testid="ai-ideas-topic" className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-emerald-200 text-sm outline-none focus:ring-2 focus:ring-emerald-300" />
        <button onClick={go} disabled={busy} data-testid="ai-ideas-btn" className="h-10 px-4 rounded-xl bg-emerald-600 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Los</button>
      </div>
      {out && <div className="rounded-2xl bg-emerald-50/60 p-4 text-sm whitespace-pre-wrap" data-testid="ai-ideas-out">{out}</div>}
      {out && !busy && <button onClick={() => { navigator.clipboard.writeText(out); toast.success("Kopiert – jetzt im Feed einfügen"); }} data-testid="ai-ideas-copy" className="text-xs font-bold text-emerald-700 hover:underline">Text kopieren</button>}
    </Card>
  );
}

function InboxItem({ c, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const suggest = async () => {
    setBusy(true);
    try { await streamWrite({ comment_id: c.id }, (t) => setText(t.replace(/\*\*/g, "").trim()), "/admin/ai/reply"); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { await api.post(`/feed/posts/${c.post_id}/comments`, { text: text.trim(), parent_id: c.id }); toast.success("Antwort gesendet 💕"); onDone(c.id); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-2xl border border-pink-100 p-3 space-y-2" data-testid={`ai-inbox-${c.id}`}>
      <div className="flex gap-2">
        <UserAvatar userId={c.user_id} name={c.name} picture={c.picture} size="w-8 h-8" />
        <div className="min-w-0 flex-1"><p className="text-xs font-bold">{c.name} <span className="font-normal text-slate-400">· {timeAgo(c.created_at)} · zu „{c.post_text}“</span></p><p className="text-sm">{c.text}</p></div>
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Deine Antwort …" data-testid={`ai-inbox-input-${c.id}`} className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-pink-100 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
        <button onClick={suggest} disabled={busy} title="Vorschlag von ChatGPT" data-testid={`ai-inbox-suggest-${c.id}`} className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}</button>
        <button onClick={send} disabled={busy || !text.trim()} data-testid={`ai-inbox-send-${c.id}`} className="h-10 w-10 rounded-xl bg-rose-600 text-white flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </div>
    </li>
  );
}

function AnswerInbox() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/ai/inbox").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <Card icon={Inbox} title={`Antwort-Postfach${list ? ` (${list.length})` : ""}`} testId="ai-inbox">
      <p className="text-xs text-slate-500">Kommentare der letzten 14 Tage unter euren Beiträgen, auf die ihr noch nicht geantwortet habt.</p>
      {!list ? <Loader2 className="w-5 h-5 animate-spin text-slate-300" /> : list.length === 0 ? <p className="text-sm text-slate-400" data-testid="ai-inbox-empty">Alles beantwortet – super! 🎉</p>
        : <ul className="space-y-2">{list.map((c) => <InboxItem key={c.id} c={c} onDone={(id) => setList((l) => l.filter((x) => x.id !== id))} />)}</ul>}
    </Card>
  );
}

export default function AiAdmin() {
  return <div className="space-y-5" data-testid="ai-admin"><Stats /><AnswerInbox /><MoodReport /><Ideas /></div>;
}
